import { AsyncPipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { map, tap } from 'rxjs';

import { ButtonComponent } from '@aprova-digital/design-system';

import { AppHandler } from '../../app.handler';
import { CustomUrlRedirectStore } from '../../custom-url-redirect.store';
import { decodeState } from '../../utils/state-handler';
import { IconComponent, LoadingSpinnerComponent, BoxErrorComponent } from '../../../@ads/components';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    AsyncPipe,
    IconComponent,
    LoadingSpinnerComponent,
    BoxErrorComponent,
    ButtonComponent,
  ],
  templateUrl: './main.layout.component.html',
  styleUrl: './main.layout.component.scss',
})
export class MainLayoutComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _appHandler = inject(AppHandler);
  private readonly _origem = inject(CustomUrlRedirectStore);

  private cityHomeUrl: string = '';

  /**
   * Endereço personalizado por onde a pessoa entrou, validado contra a allowlist: sem validar, o
   * botão de voltar viraria redirect aberto por query param.
   */
  private readonly customUrlRedirect: string = this.resolveCustomUrlRedirect();

  public loading = signal(true);
  public loadingConfig: {
    title: string;
    description: string;
  } = {
    title: 'Carregando...',
    description:
      'Este processo pode levar alguns segundos. Por favor, aguarde.',
  };

  public isBackButtonHome = signal(true);
  public hideBackButton = signal(false);

  public cityIdentity: string = this._route.snapshot.data['city']['identidade'];

  public hasError = signal(false);
  public errorConfig: {
    message: string;
    description: string;
    iconName?: string;
    iconColor?: string;
    iconSize?: string;
    actionText?: string;
    options?: {
      iconBackgroundColor?: string;
      messageHorizontalAlign?: 'left' | 'center' | 'right';
      useHtmlInMessage?: boolean;
    };
    action?: () => void;
  } = {
    message: '',
    description: '',
    actionText: '',
    action: () => {},
  };

  protected readonly data$ = this._route.data.pipe(
    tap(({ city }) => {
      this._appHandler.setCity(city);
    }),
    map(({ city }) => ({
      cityLogo: city.logo,
      cityName: city.title,
      cityPhone: city.tel,
      cityIdentity: city.identidade,
      applicationLink: city.applicationLink,
    })),
    tap((data) => {
      const subdomain = data.cityIdentity.slice(0, -2);
      // O endereço por onde a pessoa entrou vence: mandá-la para o domínio legado quebra a
      // navegação e pode formar loop com o redirect do domínio antigo (APR-3364).
      this.cityHomeUrl =
        this.customUrlRedirect || data.applicationLink || `https://${subdomain}.aprova.com.br`;
      this.cityIdentity = data.cityIdentity;
    })
  );

  ngOnInit(): void {
    this.hideBackButton.set(false);
    this.buildLoadingConfig(); // Sempre configurar os listeners
    this.authorizationCodeHandler();
  }

  private buildLoadingConfig(): void {
    this._appHandler.loadingChange().subscribe(({ loading, config }) => {
      if (config) {
        this.loadingConfig = {
          title: config.title,
          description: config.description,
        };
      }

      this.loading.set(loading);
    });

    this._appHandler.errorChange().subscribe(({ error, config }) => {
      this.hasError.set(error);

      if (config) {
        this.errorConfig = config;
      }
    });

    this._appHandler.backButtonHomeChange().subscribe((isBackButtonHome) => {
      this.isBackButtonHome.set(isBackButtonHome);
    });

    this._appHandler.hideBackButtonChange().subscribe((hideBackButton) => {
      this.hideBackButton.set(hideBackButton);
    });
  }

  /**
   * Três origens, nesta ordem: a query da primeira carga, o `state` que volta do provedor externo
   * — a URL de retorno dele é fixa e não aceita a nossa query — e a memória da aba, que cobre o
   * resto do fluxo, inclusive quem cancela no gov.br e volta para a tela de login.
   */
  private resolveCustomUrlRedirect(): string {
    const cidade = this._route.snapshot.data['city']['identidade'];
    const { customUrlRedirect, state } = this._route.snapshot.queryParams;
    const recebido = customUrlRedirect || (state ? decodeState(state).customUrlRedirect : '');

    return this._origem.remember(cidade, recebido) || this._origem.recall(cidade);
  }

  private authorizationCodeHandler(): void {
    const authorizationCode = this._route.snapshot.queryParams['authorizationCode'];
    const userId = this._route.snapshot.queryParams['user_id'];
    const customUrlRedirect = this._route.snapshot.queryParams['customUrlRedirect'];
    if (authorizationCode && userId) {
      this._router.navigate([`/${this.cityIdentity}/authorization-code`], {
        state: {
          authorizationCode,
          userId,
          customUrlRedirect,
          cityIdentity: this.cityIdentity
        }
      });
      return;
    }
  }

  async handleBackButtonOnClick() {
    this._appHandler.setLoading(true);
    if (this.isBackButtonHome()) {
      window.location.href = this.cityHomeUrl;
      return;
    }

    await this._router.navigate([`/${this.cityIdentity}`]);
    this._appHandler.setLoading(false);
    this._appHandler.clearError();
  }
}
